import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, Typography, ButtonBase } from '@mui/material';
import { EventItem } from '@scorenco/components';
import { Types } from '@scorenco/core';

import {
  GetFilteredTeamsDocument,
  GetTeamsDocument,
} from './_internal/Widget.query.generated';

type EventsListWidgetProps = {
  clubId?: number;
  teams?: { team_id?: number }[];
  sort?: string;
  daySwitch?: string;
  settings?: {
    show_name_in_club?: boolean;
  };
};

export const EventsListWidget = ({
  teams,
  sort,
  clubId,
  settings = {
    show_name_in_club: false,
  },
}: EventsListWidgetProps) => {
  const now = useMemo(() => new Date(), []);

  const getAllTeams = useQuery(GetTeamsDocument, {
    variables: {
      clubId: clubId!,
      now,
    },
    skip: !clubId || (teams?.length ?? 0) > 0,
    context: {
      role: 'anonymous',
    },
  });

  const getFilteredTeams = useQuery(GetFilteredTeamsDocument, {
    variables: {
      clubId: clubId!,
      now,
      teamsIds: teams
        ?.map((team) => team.team_id)
        .filter((id): id is number => id !== undefined),
    },
    skip: !clubId || teams?.length === 0,
    context: {
      role: 'anonymous',
    },
  });

  const widgetTeams =
    teams?.length === 0
      ? getAllTeams.data?.teams_team_detail_by_club_id
      : getFilteredTeams.data?.teams_team_detail_by_club_id;

  return (
    <Box sx={{ width: '100%', marginBottom: '1.5rem' }}>
      {widgetTeams?.map((team, idx) => {
        const lastEvent = team?.last_events?.[0]?.event_detail?.[0];
        const nextEvent = team?.next_events?.[0]?.event_detail?.[0];
        const differentCompetition =
          lastEvent?.competition?.id !== nextEvent?.competition?.id;

        return (
          <Box key={idx}>
            {lastEvent && (
              <Typography
                variant="h5"
                sx={{
                  backgroundColor: 'background.paper2',
                  textAlign: 'center',
                  padding: '0.5rem',
                  border: '1px solid #d2e0ef',
                }}
              >
                {differentCompetition
                  ? lastEvent?.competition?.name
                  : team?.team_in_season?.last_competition_with_ranking?.[0]
                      ?.name}
              </Typography>
            )}
            {lastEvent && sort !== 'next' && (
              <a
                href={`${process.env.NEXT_PUBLIC_WEB_URL}${lastEvent?.url}`}
                target="_blank"
                rel="noreferrer"
              >
                <ButtonBase
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderLeft: 'none',
                    borderRight: 'none',
                    width: '100%',
                    '&:hover': {
                      background: 'background.paper2',
                    },
                  }}
                >
                  <EventItem
                    component="a"
                    event={lastEvent as Types.Competitions_Event_Detail}
                    clubId={clubId}
                    showShortName={settings?.show_name_in_club}
                    showPlace={false}
                    showCompetition={false}
                    showDate={true}
                  />
                </ButtonBase>
              </a>
            )}
            {differentCompetition && nextEvent?.competition?.name && (
              <Typography
                variant="h5"
                sx={{
                  backgroundColor: 'background.paper2',
                  textAlign: 'center',
                  padding: '0.5rem',
                  border: '1px solid #d2e0ef',
                }}
              >
                {nextEvent?.competition?.name}
              </Typography>
            )}
            {nextEvent && sort !== 'last' && (
              <a
                href={`${process.env.NEXT_PUBLIC_WEB_URL}${nextEvent?.url}`}
                target="_blank"
                rel="noreferrer"
              >
                <ButtonBase
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderLeft: 'none',
                    borderRight: 'none',
                    width: '100%',
                    '&:hover': {
                      background: 'background.paper2',
                    },
                  }}
                >
                  <EventItem
                    event={nextEvent as Types.Competitions_Event_Detail}
                    clubId={clubId}
                    showShortName={settings?.show_name_in_club}
                    showPlace={false}
                    showCompetition={false}
                    showDate={true}
                  />
                </ButtonBase>
              </a>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
